import { animate, state, style, transition, trigger } from "@angular/animations";
import { SelectionModel } from "@angular/cdk/collections";
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewChildren, ViewEncapsulation } from "@angular/core";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { Select, Store } from "@ngxs/store";
import { IDivision } from "@zonar-ui/auth/lib/models/company.model";
import { startOfDay, subDays } from "date-fns";
import { Observable, Subject, Subscription } from "rxjs";
import { debounceTime, delay, distinctUntilChanged, filter, skip } from "rxjs/operators";
import { AppState } from "src/app/app.state";
import { EnabledFilters, FilterCardComponent } from "src/app/components/filter-card/filter-card.component";
import { InfoDialogComponent } from "src/app/components/info-dialog/info-dialog.component";
import { PaginatorComponent } from "src/app/components/paginator/paginator.component";
import { TabConfig } from "src/app/components/tab-control/tab-control.component";
import { translateAndFormat } from "src/app/i18next";
import { formatDate, formatDateTime } from "src/app/i18next/formatDate";
import { FilterCardQueryStrings, QueryStrings } from "src/app/models/emitter-events.models";
import { ExportType } from "src/app/models/exportType";
import { MissingInspection } from "src/app/models/inspection.model";
import { LanguageDictionary } from "src/app/models/language-dictionary.model";
import { SplitAtFirstDelimiterPipe } from "src/app/pipes/split-at-first-delimiter.pipe";
import { ExportService } from "src/app/services/export/export.service";
import { LocalStateService } from "src/app/services/local-state/local-state.service";
import { LoggerService } from "src/app/services/logger.service";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";
import { PageInformation } from "src/app/services/previous-page.service";
import { environment } from "src/environments/environment";
import { formatIso } from "src/utils/formatIso/formatIso";
import { isDefined } from "src/utils/isDefined/isDefined";
import { meterToMile } from "src/utils/meterToMile/meterToMile";
import { newDate } from "src/utils/newDate/newDate";
import { secondsToHourMinuteSecond } from "src/utils/secondsToHourMinute/secondsToHourMinute";
import { GetMissingInspectionsList, MissingInspectionsState } from "./state/missing-inspection.state";

export const debounceMs = 100;
export interface MissingInspectionSortState {
	sortActive: string;
	sortDirection: string;
	reportFocus: string;
}

interface TabConfigData {
	reportFocus: "gps" | "assets" | "inspectors";
}

export class MissingInspectionViewModel {
	division: string;
	inspection: {
		type: string[];
	};
	startTime: string | string[];
	endTime: string | string[];
	isoTime: string[];
	asset: {
		id: string;
		name: string;
		type: string;
	};
	inspector: string;
	zoneLayout: string;
	index?: number;
	distance: number | string;
	duration: number | string;
}

export type MissingInspectionsReportType = "inspectors" | "assets" | "gps";

const stateKey = "missing-inspections";

@Component({
	selector: "app-missing-inspections",
	templateUrl: "./missing-inspections.component.html",
	styleUrls: ["./missing-inspections.component.scss"],
	encapsulation: ViewEncapsulation.None,
	animations: [
		// expandable rows example: https://material.angular.io/components/table/examples
		// added void: https://github.com/angular/components/issues/11990
		trigger("detailExpand", [
			state("collapsed, void", style({ height: "0px", minHeight: "0" })),
			state("expanded", style({ height: "*" })),
			transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
			transition("expanded <=> void", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
		]),
		trigger("rotatedState", [
			state("down", style({ transform: "rotate(0)" })),
			state("up", style({ transform: "rotate(180deg)" })),
			transition("down <=> up", animate("225ms cubic-bezier(0.4,0.0,0.2,1)")),
		]),
	],
})
export class MissingInspectionsComponent implements OnInit, OnDestroy {
	@ViewChild("appPaginator") paginatorComponent!: PaginatorComponent;
	@ViewChild("infoButton", { read: ElementRef }) infoButton!: ElementRef<HTMLButtonElement>;

	config = {
		initialSelection: [],
		allowMultiSelect: true,
	};
	defaultState: MissingInspectionSortState = {
		sortActive: "start-time",
		sortDirection: "desc",
		reportFocus: "assets",
	};
	enabledFilters: EnabledFilters = {
		assetId: false,
		assetType: false,
		customDateRange: false,
		dateRange: false,
		defectType: false,
		inspectionType: false,
		homeLocation: false,
		inspector: false,
		page: "",
		severity: false,
		tableFilter: false,
		resetFilterButton: false,
		searchBar: false,
		inspectionStatus: false,
	};
	companyId: string;
	divisions: Array<string>;
	hasTCU = undefined;
	inspectionData: MissingInspectionViewModel[] = [];
	currentPageSize: number;
	isInitiallyLoading = true;
	missingInspectionsListCount = 0;
	pageIndex = 0;
	pageSize = 10;
	pageSizeOptions = [10, 25, 50, 75, 100];
	modifySearchString: string = this.translate("please modify your search and try again", "capitalize");
	reportFocus: MissingInspectionsReportType = "assets";
	rotateState: string[] = [];
	selectionModel: SelectionModel<any>;
	sortActive = "start-time";
	sortDirection = "desc";
	state: MissingInspectionSortState;
	subscriptions = new Subscription();
	zonarMapUrl = environment.environmentConstants.ZONAR_MAP_URL;
	missingInspectionsData: MissingInspection[] = [];
	missingInspectionsDataTable: MissingInspectionViewModel[] = [];
	currentQueryStrings: QueryStrings = {
		page: "1",
		perPage: this.pageSize.toString(),
		sort: "startTime",
		sortDir: "desc",
		missingReportFocus: this.reportFocus,
		dates: [{ startTime: startOfDay(subDays(newDate(), 7)), endTime: newDate() }],
	};
	displayLiveMaps = true;

	view: MissingInspectionsReportType = "assets";
	isOldMIR: boolean;

	splitPipe = new SplitAtFirstDelimiterPipe();
	inTransit = this.translate("in transit", "title");
	dataSource = new MatTableDataSource<MissingInspectionViewModel>();
	private debounceFilterUpdates$ = new Subject<URLSearchParams>();

	@ViewChild(FilterCardComponent) filterCardComponent: FilterCardComponent;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChildren("matCheckbox") checkboxes: MatCheckbox;

	sort: MatSort;

	@Select(AppState.getSelectedCompanyId) selectedCompanyId$: Observable<string>;
	@Select(AppState.selectCompanyHasTCU) hasTCU$: Observable<boolean>;
	@Select(AppState.selectLanguageDictionary) languageDictionary$: Observable<LanguageDictionary>;
	@Select(AppState.selectUserDivisions) userDivisions$: Observable<Array<IDivision>>;
	@Select(AppState.selectPageInformation) pageInformation$: Observable<PageInformation>;
	@Select(MissingInspectionsState.getMissingInspectionsList) missingInspectionsList$: Observable<MissingInspection[]>;
	@Select(MissingInspectionsState.getMissingInspectionsListCount) missingInspectionsListCount$: Observable<number>;
	@Select(MissingInspectionsState.missingInspectionsUpdate) getMissingInspectionsUpdate$: Observable<number>;

	tabs: Array<TabConfig<unknown>> = [];
	tabsTmpl: Array<TabConfig<unknown>> = [
		{
			id: "assets",
			label: "Assets (All)",
			selected: true,
			data: {
				reportFocus: "assets",
			},
		},
		{
			id: "inspectors",
			label: "Inspectors",
			selected: false,
			data: {
				reportFocus: "inspectors",
			},
		},
	];
	showNewReportBtn = false;

	sortQueryStringValues = () => ({
		"start-time": "startTime",
		"asset-id": "assetName",
		"asset-number": "assetName",
		"asset-type": "zoneLayoutName",
		"inspection-type": "inspectionType",
		"inspector-name": "inspectorName",
		"end-time": "endTime",
		"trip-duration": "duration",
		"distance-traveled": "distance",
	});

	constructor(
		public dialog: MatDialog,
		private memCacheService: MemCacheService,
		private exportService: ExportService,
		private logger: LoggerService,
		public store: Store,
		private localSvc: LocalStateService,
		private route: ActivatedRoute,
		private router: Router,
	) {}

	updateTabs(hasTCU: boolean) {
		if (hasTCU) {
			this.reportFocus = "gps";
			this.sortActive = "start-time";
			this.tabs = [
				{
					id: "gps",
					label: "Assets (GPS enabled)",
					selected: true,
					data: {
						reportFocus: "gps",
					},
				},
				...this.tabsTmpl.map(tab => ({ ...tab, selected: false })),
			];
		} else {
			this.tabs = this.tabsTmpl.map(tab => {
				if (tab.id === "assets") {
					tab.label = "Assets";
				}

				return tab;
			});
		}
		this.enabledFilters = this.resetFilters(this.reportFocus);
		this.view = this.reportFocus;
		this.handleFilterValues({ ...this.currentQueryStrings, missingReportFocus: this.view });
	}

	ngOnInit() {
		this.isOldMIR = this.route.routeConfig?.path === "missing-inspections-v1";
		const { keepCurrentMIR } = history.state || {};
		if (!this.isOldMIR && !keepCurrentMIR) {
			this.router.navigate(["/missing-inspections-v1"], { state: { keepCurrentMIR: true } });
			return;
		}
		this.showNewReportBtn = !this.localSvc.getPXLogin();
		this.currentPageSize = 10;
		this.getDataSource().sort = this.sort;
		this.getDataSource().paginator = this.paginator;
		this.memCacheService.setValue("isLoadingPage", true);

		this.displayLiveMaps = !this.localSvc.getPXLogin();

		// only server-side sorting
		this.getDataSource().sortingDataAccessor = () => null;

		// Set filter views
		const enabledFilters = {
			assetId: true,
			assetType: true,
			customDateRange: true,
			dateRange: false,
			defectType: false,
			inspectionType: true,
			homeLocation: true,
			inspector: true,
			page: "missing-inspections",
			severity: false,
			tableFilter: false,
			resetFilterButton: true,
			searchBar: false,
			inspectionStatus: false,
		};
		this.enabledFilters = this.isOldMIR
			? { ...enabledFilters }
			: { ...enabledFilters, distanceTraveled: true, tripDuration: true };

		this.subscriptions.add(
			this.hasTCU$.pipe(filter(isDefined)).subscribe(hasTCU => {
				this.hasTCU = hasTCU;
				this.updateTabs(hasTCU);
			}),
		);

		this.subscriptions.add(
			this.selectedCompanyId$.pipe(filter(isDefined)).subscribe(selectedCompanyId => {
				this.memCacheService.clear(stateKey);
				this.companyId = selectedCompanyId;
			}),
		);

		this.subscriptions.add(
			this.userDivisions$.pipe(filter(isDefined)).subscribe(divisions => {
				this.divisions = divisions.map(({ id }) => id);
			}),
		);

		this.subscriptions.add(
			this.languageDictionary$.pipe(filter(isDefined)).subscribe(() => {
				// Subscribe to missing inspections list count
				this.subscriptions.add(
					this.missingInspectionsListCount$.subscribe((count: number) => {
						this.missingInspectionsListCount = count;

						//initialize all triangles in view to down since no expansion should happen on init
						this.rotateState = new Array(this.missingInspectionsListCount).fill("down");
					}),
				);
			}),
		);

		// debounce filter updates - multiple will be generated when the filter card is cleard
		this.subscriptions.add(
			this.debounceFilterUpdates$
				.pipe(debounceTime(debounceMs))
				.subscribe(this.handleFilterValuesDebounced.bind(this)),
		);

		this.getDataSource().sort = this.sort;
		this.getDataSource().paginator = this.paginator;

		this.initState();

		// Subscribe to missing inspections list
		this.subscriptions.add(
			this.missingInspectionsList$
				.pipe(distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)))
				.subscribe((missingInspections: MissingInspection[]) =>
					this.missingInspectionListSubscribeHandler(missingInspections),
				),
		);

		// Subscribe to memcache service
		this.subscriptions.add(
			this.memCacheService
				.cleared$()
				.pipe(filter(context => context === "missing-inspections"))
				.subscribe(() => {
					this.defaultState.sortActive = "start-time";
					this.defaultState.sortDirection = "desc";
					this.memCacheService.setValue(stateKey, this.defaultState);
					this.initState();
				}),
		);

		this.subscriptions.add(
			this.getMissingInspectionsUpdate$.pipe(skip(1)).subscribe(() => {
				this.handleMissingInspectionsUpdate();
			}),
		);

		this.subscriptions.add(
			this.memCacheService.cacheChanged$(stateKey).subscribe((value: MissingInspectionSortState) => {
				this.state = value;
			}),
		);

		this.subscriptions.add(
			this.pageInformation$
				.pipe(
					filter(pageInformation => {
						const mirRoutes = ["/missing-inspections-v1", "/missing-inspections"];
						const uuidRegex =
							"[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}";
						const assetDetailsUrlRegex = new RegExp(`^/company/${uuidRegex}/asset-details/${uuidRegex}$`);

						// Reset filter card when navigating to old/new Missing Inspections Report
						// from any pages excluding the asset details page
						return (
							!!pageInformation &&
							!assetDetailsUrlRegex.test(pageInformation?.previousPageUrl) &&
							mirRoutes.includes(pageInformation?.currentPageUrl)
						);
					}),
					delay(0),
				)
				.subscribe(() => {
					this.filterCardComponent.resetCheckbox = false;
					this.filterCardComponent.clearAllState();
				}),
		);
	}

	handleMissingInspectionsUpdate() {
		if (this.getDataSource().data.length === 0 && this.inspectionData.length > 0) {
			this.getDataSource().data = this.inspectionData;
		} else {
			this.isInitiallyLoading = false;
		}
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	receiveSortObj(sort: MatSort) {
		this.sort = sort;
		this.sort.disableClear = true;
		this.getDataSource().sort = sort;
	}

	/**
	 * Initialize sorting state
	 */
	initState() {
		this.state = this.getState();

		if (!this.state) {
			this.state = {
				sortActive: this.sortActive,
				sortDirection: this.sortDirection,
				reportFocus: this.reportFocus,
			};
		} else {
			this.state = {
				sortActive: "start-time",
				sortDirection: "desc",
				reportFocus: this.reportFocus,
			};
		}

		this.sortActive = this.state.sortActive;
		this.sortDirection = this.state.sortDirection;
		this.reportFocus = this.state.reportFocus as MissingInspectionsReportType;
	}

	/**
	 * Update data on filter change
	 * @param filterQueries query strings for filters
	 */
	handleFilterValues(filterQueries: QueryStrings) {
		if (typeof this.hasTCU === "undefined") return;

		const { page, perPage, sort, sortDir, missingReportFocus } = this.currentQueryStrings;
		const { distance, duration, ...remainingFilterQueries } = filterQueries;
		this.currentQueryStrings = {
			page,
			perPage,
			sort,
			sortDir,
			missingReportFocus,
			...(this.isOldMIR ? remainingFilterQueries : filterQueries),
		};

		if (this.reportFocus !== filterQueries.missingReportFocus) {
			this.isInitiallyLoading = true;

			this.currentQueryStrings = {
				...this.currentQueryStrings,
				sort: this.sortQueryStringValues()[this.sortActive],
				sortDir: this.sortDirection,
			};
		}

		this.updateTable(true);
		this.flushState();
	}

	currentlySelectedMissingInspections() {
		const missingInspections = this.selectionModel.selected;
		missingInspections.sort((a, b) => a.index - b.index);

		return missingInspections;
	}

	/**
	 * Export the data to a CSV file
	 */
	exportCSV() {
		this.exportService.exportMissingInspectionData(
			this.currentlySelectedMissingInspections(),
			this.reportFocus,
			ExportType.CSV,
			this.isOldMIR,
		);
	}

	/**
	 * Export the data to a PDF file
	 */
	exportPDF() {
		this.exportService.exportMissingInspectionData(
			this.currentlySelectedMissingInspections(),
			this.reportFocus,
			ExportType.PDF,
			this.isOldMIR,
		);
	}

	exportAllCSV() {
		this.exportService.exportAllMissingInspections(
			this.missingInspectionsListCount,
			10000,
			1000,
			this.companyId,
			this.buildQueryStrings(),
			this.isOldMIR,
			(missingInspections: MissingInspection[]) => this.transformToMissingInspectionView(missingInspections),
		);
	}

	/**
	 * Sort table data
	 * @param event Sorting event
	 */
	onMatSortChange(event: Sort) {
		this.currentQueryStrings = {
			...this.currentQueryStrings,
			sort: this.sortQueryStringValues()[event.active],
			sortDir: event.direction,
		};

		this.updateTable(true);
		this.selectionModel?.clear();

		this.sortActive = event.active;
		this.sortDirection = event.direction;

		this.flushState();
	}

	/**
	 * Set the current state of the sorts
	 */
	flushState() {
		this.memCacheService.setValue<MissingInspectionSortState>(stateKey, {
			...this.defaultState,
			sortActive: this.sortActive,
			sortDirection: this.sortDirection,
			reportFocus: this.reportFocus,
		});
	}

	/**
	 * Get the current state of the sorts
	 * @returns Current state of sorts
	 */
	getState() {
		return this.memCacheService.getValue<MissingInspectionSortState>(stateKey);
	}

	/**
	 * Data source for the table
	 * @returns Table data source
	 */
	getDataSource() {
		return this.dataSource;
	}

	/**
	 *
	 * @param event Mouse event
	 * @param row row data
	 * @returns Toggle the checkbox selection for the row or returns null if it didn't received the event
	 */
	handleCheckboxChange(event: MouseEvent, row: MissingInspectionViewModel) {
		return event ? this.selectionModel.toggle(row) : null;
	}

	/**
	 * Stop event propagation on checkbox click
	 * @param event Mouse event
	 */
	handleCheckboxClick(event: MouseEvent) {
		event.stopPropagation();
	}

	/**
	 * Label for the table checkbox
	 * @param row possible row selected on the table
	 * @returns Aria label for the row
	 */
	checkboxLabel(row: MissingInspectionViewModel | undefined) {
		return !row
			? `${this.isAllSelected() ? "deselect" : "select"} all` // Master checkbox selected
			: `${this.selectionModel.isSelected(row) ? "deselect" : "select"} row`; // Individual row selected
	}

	/**
	 * Checks if all rows are selected
	 * @returns True if all rows are selected
	 */
	isAllSelected() {
		const numSelected = this.selectionModel.selected.length;
		const numRows = this.getDataSource().data.length;
		return numSelected === numRows;
	}

	/**
	 * Toggles between all and none row selected
	 */
	masterToggle() {
		this.isAllSelected()
			? this.selectionModel?.clear()
			: this.getDataSource().data.forEach(row => this.selectionModel?.select(row));
	}

	/**
	 * Rotate chevron icon
	 * @param i index of the row
	 */
	rotate(i: number) {
		this.rotateState = this.rotateState.map((rotation: string, k: number) => {
			if (k === i) {
				return rotation === "down" ? "up" : "down";
			} else {
				return rotation;
			}
		});
	}

	/**
	 * Paginate results
	 * @param query Page query information
	 */
	onQueryChange(query: URLSearchParams) {
		this.pageIndex = parseInt(query.get("page"));
		this.pageSize = parseInt(query.get("perPage"));

		this.currentQueryStrings = {
			...this.currentQueryStrings,
			...Object.fromEntries(query),
		};

		this.updateTable();

		// reset selected rows
		this.selectionModel.clear();

		// reset chevrons
		this.rotateState = new Array(this.missingInspectionsListCount).fill("down");
	}

	/**
	 * Update data table
	 * @param resetPaginator boolean to reset paginator
	 */
	updateTable(resetPaginator: boolean = false) {
		if (this.companyId) {
			// reset paginator on filter change back to first page
			if (resetPaginator) {
				this.currentQueryStrings = {
					...this.currentQueryStrings,
					page: "1",
					perPage: this.pageSize.toString(),
				};
				this.pageIndex = 0;
				this.paginatorComponent?.resetPageIndex();
			}

			// Get missing inspections list
			this.store.dispatch(new GetMissingInspectionsList(this.companyId, this.buildQueryStrings(), this.isOldMIR));
		} else {
			this.logger.log(`updateTable() called, but companyId hasn't been populated yet`);
		}
	}

	/**
	 * Missing Inspections Subscription Handler
	 * @param missingInspections Missing Inspection response data
	 */
	missingInspectionListSubscribeHandler(missingInspections: MissingInspection[]) {
		// necessary because inspections is null initially
		if (missingInspections !== null && missingInspections !== undefined) {
			this.isInitiallyLoading = false;
			if (missingInspections && missingInspections.length > 0) {
				this.missingInspectionsData = missingInspections;
				this.missingInspectionsDataTable = this.transformToMissingInspectionView(missingInspections);
				const dataToDisplay = this.missingInspectionsDataTable;

				this.getDataSource().data = dataToDisplay;
				this.inspectionData = dataToDisplay;
			} else {
				// inspections has been cleared
				this.getDataSource().data = [];
				this.inspectionData = [];
			}
		}
	}

	/**
	 * Open info dialog
	 */
	openInfoDialog() {
		const dialogRef = this.dialog.open(InfoDialogComponent, {
			width: "524px",
			autoFocus: ".dialog-action button",
			restoreFocus: false,
		});
		dialogRef.afterClosed().subscribe(() => {
			setTimeout(() => this.infoButton.nativeElement.focus());
		});
	}

	translate(key: string, formatType: "capitalize" | "uppercase" | "title" | "camelCase") {
		return translateAndFormat(key, formatType);
	}
	/**
	 * A tab was selected
	 */
	selectTab(data: TabConfigData) {
		/*
		 * wait for the current operation to complete to avoid an impatient user generating
		 * an indefinite number of requests.
		 */
		if (this.view === data.reportFocus) return;
		this.isInitiallyLoading = true;
		this.view = data.reportFocus;
		this.reportFocus = data.reportFocus;

		// reset filter card
		this.enabledFilters = this.resetFilters(this.reportFocus);
		this.filterCardComponent.resetCheckbox = true;
		this.filterCardComponent.clearAllState();

		// reset query params
		this.pageSize = 10;
		this.sortActive = this.defaultState.sortActive;
		this.sortDirection = this.defaultState.sortDirection;
		this.currentQueryStrings = {
			page: "1",
			perPage: this.pageSize.toString(),
			sort: this.sortQueryStringValues()[this.sortActive],
			sortDir: this.sortDirection,
			missingReportFocus: this.reportFocus,
			dates: [{ startTime: startOfDay(subDays(newDate(), 7)), endTime: newDate() }],
		};

		this.updateTable(true);

		// reset selected rows
		this.selectionModel.clear();

		// reset chevrons
		this.rotateState = new Array(this.missingInspectionsListCount).fill("down");
		this.filterCardComponent.resetCheckbox = false;
	}

	/**
	 * Handle filter values on Debounce
	 * @param filterQueries Filter query parameters
	 */
	handleFilterValuesDebounced(filterQueries: FilterCardQueryStrings): void {
		this.pageIndex = 1;

		const { page, perPage, sort, sortDir, missingReportFocus } = this.currentQueryStrings;
		this.currentQueryStrings = {
			page,
			perPage,
			sort,
			sortDir,
			missingReportFocus,
			...filterQueries,
		};

		this.updateTable(true);
	}

	resetFilters(reportFocus: string) {
		const enabledFilters = {
			assetId: false,
			assetType: false,
			customDateRange: true,
			dateRange: false,
			defectType: false,
			inspector: false,
			inspectionType: true,
			homeLocation: true,
			page: "missing-inspections",
			severity: false,
			tableFilter: false,
			resetFilterButton: true,
			searchBar: false,
			inspectionStatus: false,
			tripDuration: false,
			distanceTraveled: false,
		};

		switch (reportFocus) {
			case "gps":
				enabledFilters.assetId = true;
				enabledFilters.assetType = true;
				if (!this.isOldMIR) {
					enabledFilters.distanceTraveled = true;
					enabledFilters.tripDuration = true;
				}
				break;

			case "assets":
				enabledFilters.assetId = true;
				enabledFilters.assetType = true;
				break;

			case "inspectors":
				enabledFilters.inspector = true;
				break;
		}

		return enabledFilters;
	}

	navigateToMIR() {
		const mirUrl = this.isOldMIR ? "/missing-inspections" : "/missing-inspections-v1";
		this.router.navigate([mirUrl], { state: { keepCurrentMIR: true } });
	}

	private transformToMissingInspectionView(missingInspections: MissingInspection[]): MissingInspectionViewModel[] {
		return missingInspections.map((missingInspection, index) => ({
			division: missingInspection.divisions[0],
			inspection: {
				type: this.splitPipe.transform(missingInspection.inspectionType),
			},
			startTime:
				this.reportFocus === "gps"
					? this.splitPipe.transform(
							formatDateTime(newDate(missingInspection.startTime || missingInspection.timestamp)),
					  )
					: formatDate(newDate(missingInspection.startTime)),
			isoTime: this.splitPipe.transform(
				formatIso(newDate(missingInspection.startTime || missingInspection.timestamp).toISOString()),
			),
			asset: {
				id: missingInspection.assetId,
				name: missingInspection.assetName,
				type: missingInspection.zoneLayoutName,
			},
			inspector: missingInspection.inspectorName,
			zoneLayout: missingInspection.zoneLayoutName,
			index,
			duration:
				missingInspection.duration || missingInspection.endTime
					? secondsToHourMinuteSecond(missingInspection.duration)
					: this.inTransit,
			distance:
				missingInspection.distance || missingInspection.endTime
					? meterToMile(missingInspection.distance)
					: this.inTransit,
			endTime: missingInspection.endTime
				? this.splitPipe.transform(formatDateTime(newDate(missingInspection.endTime)))
				: "-",
		}));
	}

	private buildQueryStrings(): QueryStrings {
		let queryStrings = { ...this.currentQueryStrings };
		const hasDivisionIds = queryStrings.divisionIds;

		if (!hasDivisionIds && this.divisions) {
			queryStrings = {
				...queryStrings,
				divisionIds: this.divisions,
			};
		}

		if (this.reportFocus === "gps") {
			const { timezone, inspectorIds, ...remaining } = queryStrings;
			queryStrings = { ...remaining };
		} else if (this.reportFocus === "inspectors") {
			const { assetIds, zoneLayoutIds, ...remaining } = queryStrings;
			queryStrings = { ...remaining };
		} else {
			const { inspectorIds, ...remaining } = queryStrings;
			queryStrings = { ...remaining };
		}

		return queryStrings;
	}
}
